/**
 * usePagination — client-side pagination state for an in-memory list.
 *
 * The full list is expected to already be in memory (fetched elsewhere); this
 * hook only slices it into pages. `pageSize` defaults to 15 and is exposed via
 * `setPageSize` so a page-size selector (e.g. 15/25/50) can be added later
 * without reworking callers.
 *
 * `page` is 0-based. The returned `page` is always clamped into
 * [0, pageCount - 1], so when the list shrinks (e.g. after a delete) the
 * current page never points past the last page and `pageItems` never slices
 * out of range — there is no one-render empty flash.
 */
import { useEffect, useMemo, useState } from "react";

export function usePagination(items, { pageSize: initialPageSize = 15 } = {}) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const [page, setPageState] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const pageCount = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(page, 0), pageCount - 1);

  // Reconcile stored page with the clamped value (e.g. after the list shrinks
  // or pageSize grows). Slicing already uses safePage, so this only keeps the
  // stored state honest for subsequent next()/prev() calls.
  useEffect(() => {
    if (page !== safePage) setPageState(safePage);
  }, [page, safePage]);

  const pageItems = useMemo(
    () => list.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [list, safePage, pageSize]
  );

  const setPage = (n) => {
    const target = typeof n === "function" ? n(safePage) : n;
    setPageState(Math.min(Math.max(target, 0), pageCount - 1));
  };

  const next = () => setPageState((p) => Math.min(p + 1, pageCount - 1));
  const prev = () => setPageState((p) => Math.max(p - 1, 0));

  const setPageSize = (n) => {
    setPageSizeState(n);
    setPageState(0);
  };

  const canNext = safePage < pageCount - 1;
  const canPrev = safePage > 0;

  return {
    pageItems,
    page: safePage,
    pageCount,
    setPage,
    next,
    prev,
    canNext,
    canPrev,
    pageSize,
    setPageSize,
  };
}
